'use client';
import { type FC, type PropsWithChildren, type ReactNode, useEffect, useState } from 'react';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { cn } from '@/app/utils';

interface SettingsItemProps {
  title: string | ReactNode;
  info?: string | ReactNode;
  extra?: ReactNode;
  className?: string;
  labelClassName?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  tooltipContentClassName?: string;
  itemClassName?: string;
}

/**
 * 带样式的设置项通用组件
 * 用于统一展示各种设置选项的容器组件
 *
 * @param title 设置项标题
 * @param info 设置项提示信息（可选）
 * @param extra 提示信息右边的额外信息（可选）
 * @param className 自定义样式类
 * @param children 子组件，通常是具体的设置控件
 * @param defaultOpen 是否默认打开提示信息
 */
export const SettingsItem: FC<PropsWithChildren<SettingsItemProps>> = ({
  title,
  info,
  extra,
  children,
  className,
  labelClassName,
  defaultOpen = false,
  onOpenChange,
  tooltipContentClassName,
  itemClassName,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);
  return (
    <div className={cn('flex gap-1 text-sm text-system-white md:flex-col md:items-start md:gap-2', className)}>
      <div className={cn('flex min-h-10 min-w-0 flex-1 items-center gap-0.5 md:w-full', labelClassName)}>
        <span className="min-w-0 text-system-white md:text-system-white/50">{title}</span>
        {info && (
          <CustomTooltip
            content={info}
            disableHoverableContent={true}
            align="start"
            open={open}
            onOpenChange={(open) => {
              setOpen(open);
              onOpenChange?.(open);
            }}
            contentClassName={tooltipContentClassName}
          >
            <BaseLineExclamation className="shrink-0 text-sm text-system-text05" />
          </CustomTooltip>
        )}
        {extra}
      </div>
      <div className={cn('min-w-[256px] flex-1 md:w-full md:min-w-0', itemClassName)}>{children}</div>
    </div>
  );
};
